import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Linking, Alert, TouchableOpacity } from 'react-native';
import { Card, Title, Text, Button, Chip, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import apiService, { Distillery, Rum } from '../services/ApiService';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { ThemedContainer } from '../components/ThemedContainer';

interface RouteParams {
  distilleryId: number;
}

const DistilleryDetailScreen: React.FC = () => {
  const [distillery, setDistillery] = useState<Distillery | null>(null);
  const [distilleryRums, setDistilleryRums] = useState<Rum[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { themeData } = useCustomTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const { distilleryId } = route.params as RouteParams;

  useEffect(() => {
    loadDistilleryData();
  }, [distilleryId]);

  const loadDistilleryData = async () => {
    try {
      setLoading(true);
      const distilleryData = await apiService.getDistilleryById(distilleryId);
      if (distilleryData) {
        setDistillery(distilleryData);
        // Load rums from this distillery
        try {
          const rums = await apiService.getRumsByDistillery(distilleryId);
          setDistilleryRums(rums);
        } catch (error) {
          console.log('No rums found for this distillery');
          setDistilleryRums([]);
        }
      }
    } catch (error) {
      console.error('Error loading distillery data:', error);
      Alert.alert('Error', 'Failed to load distillery data');
    } finally {
      setLoading(false);
    }
  };

  const handleWebsite = () => {
    if (distillery?.website) {
      Linking.openURL(distillery.website);
    }
  };

  const handleRumPress = (rum: Rum) => {
    // @ts-ignore - Navigation type issue
    navigation.navigate('RumDetail', { rum: rum });
  };

  if (loading) {
    return (
      <ThemedContainer style={styles.container}>
        <Text style={[styles.loadingText, { color: themeData.colors.text }]}>
          Loading distillery details...
        </Text>
      </ThemedContainer>
    );
  }

  if (!distillery) {
    return (
      <ThemedContainer style={styles.container}>
        <Text style={[styles.errorText, { color: themeData.colors.text }]}>
          Distillery not found
        </Text>
      </ThemedContainer>
    );
  }

  return (
    <ThemedContainer style={styles.container}>
      <ScrollView style={[styles.scrollView, { backgroundColor: themeData.colors.background }]}>
        {/* Header */}
        <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackground }]}>
          <Card.Content>
            <Title style={[styles.title, { color: themeData.colors.text }]}>
              {distillery.name}
            </Title>
            <Text style={[styles.subtitle, { color: themeData.colors.textSecondary }]}>
              {[
                distillery.city,
                distillery.region,
                distillery.country
              ].filter(Boolean).join(', ')}
            </Text>
          </Card.Content>
        </Card>

        {/* Contact & Location */}
        <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackgroundVariant }]}>
          <Card.Content>
            <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>
              📍 Contact & Location
            </Title>
            <View style={styles.infoGrid}>
              {distillery.website && (
                <View style={styles.infoItem}>
                  <Text style={[styles.infoLabel, { color: themeData.colors.highlight1 }]}>Website</Text>
                  <Button 
                    mode="text" 
                    onPress={handleWebsite}
                    textColor={themeData.colors.highlight1}
                  >
                    Visit Website
                  </Button>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>

        {/* Spirits & Production */}
        <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackground }]}>
          <Card.Content>
            <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>
              🥃 Spirits & Production
            </Title>
            <View style={styles.infoGrid}>
              <View style={styles.infoItem}>
                <Text style={[styles.infoLabel, { color: themeData.colors.highlight2 }]}>Facility Type</Text>
                <Text style={[styles.infoValue, { color: themeData.colors.text }]}>
                  {distillery.facility_type || 'Unknown'}
                </Text>
              </View>
              {distillery.founded_year && (
                <View style={styles.infoItem}>
                  <Text style={[styles.infoLabel, { color: themeData.colors.highlight3 }]}>Founded</Text>
                  <Text style={[styles.infoValue, { color: themeData.colors.text }]}>
                    {distillery.founded_year}
                  </Text>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>

        {/* Additional Information */}
        <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackgroundVariant }]}>
          <Card.Content>
            <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>
              📋 Additional Information
            </Title>
            <View style={styles.infoGrid}>
              {distillery.parent_company && (
                <View style={styles.infoItem}>
                  <Text style={[styles.infoLabel, { color: themeData.colors.highlight4 }]}>Parent Company</Text>
                  <Text style={[styles.infoValue, { color: themeData.colors.text }]}>
                    {distillery.parent_company}
                  </Text>
                </View>
              )}
              {distillery.state_province && (
                <View style={styles.infoItem}>
                  <Text style={[styles.infoLabel, { color: themeData.colors.highlight1 }]}>State/Province</Text>
                  <Text style={[styles.infoValue, { color: themeData.colors.text }]}>
                    {distillery.state_province}
                  </Text>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>

        {/* Rums from this Distillery */}
        {distilleryRums.length > 0 && (
          <Card style={[styles.card, { backgroundColor: themeData.colors.cardBackground }]}>
            <Card.Content>
              <Title style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                🥃 Rums from this Distillery
              </Title>
              <View style={styles.rumsContainer}>
                {distilleryRums.map((rum, index) => (
                  <TouchableOpacity key={`${rum.id}-${index}`} onPress={() => handleRumPress(rum)}>
                    <Card style={[styles.rumCard, { backgroundColor: themeData.colors.cardBackgroundVariant }]}>
                      <Card.Content>
                        <Text style={[styles.rumName, { color: themeData.colors.text }]}>
                          {rum.name}
                        </Text>
                        <Text style={[styles.rumDetails, { color: themeData.colors.textSecondary }]}>
                          {rum.country} • {rum.proof ? `${rum.proof}% ABV` : 'ABV Unknown'}
                        </Text>
                        {rum.description && (
                          <Text style={[styles.rumDescription, { color: themeData.colors.textSecondary }]} numberOfLines={2}>
                            {rum.description}
                          </Text>
                        )}
                      </Card.Content>
                    </Card>
                  </TouchableOpacity>
                ))}
              </View>
            </Card.Content>
          </Card>
        )}
             </ScrollView>

       {/* Custom Bottom Navigation */}
       <View style={[styles.bottomNav, { backgroundColor: themeData.colors.surface, borderTopColor: themeData.colors.border }]}>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Home' as never)}
         >
           <Ionicons name="home-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Home</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Tiki Bars' as never)}
         >
           <Ionicons name="beer-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Bars</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Cocktails' as never)}
         >
           <Ionicons name="wine-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Cocktails</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Rums' as never)}
         >
           <Ionicons name="flask-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Rums</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Distilleries' as never)}
         >
           <Ionicons name="business-outline" size={24} color={themeData.colors.primary} />
           <Text style={[styles.navText, { color: themeData.colors.primary }]}>Distilleries</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Culture' as never)}
         >
           <Ionicons name="library-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Culture</Text>
         </TouchableOpacity>
         <TouchableOpacity 
           style={styles.navItem} 
           onPress={() => navigation.navigate('Blog' as never)}
         >
           <Ionicons name="newspaper-outline" size={24} color={themeData.colors.textSecondary} />
           <Text style={[styles.navText, { color: themeData.colors.textSecondary }]}>Blog</Text>
         </TouchableOpacity>
       </View>
     </ThemedContainer>
   );
 };

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  card: {
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  infoItem: {
    flex: 1,
    minWidth: '45%',
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 16,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  rumsContainer: {
    gap: 12,
  },
  rumCard: {
    marginBottom: 8,
  },
  rumName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  rumDetails: {
    fontSize: 14,
    marginBottom: 4,
  },
  rumDescription: {
    fontSize: 14,
  },
  loadingText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 50,
  },
     errorText: {
     fontSize: 16,
     textAlign: 'center',
     marginTop: 50,
   },
   bottomNav: {
     flexDirection: 'row',
     justifyContent: 'space-around',
     paddingVertical: 10,
     position: 'absolute',
     bottom: 0,
     left: 0,
     right: 0,
     zIndex: 10,
     borderTopWidth: 1,
   },
   navItem: {
     alignItems: 'center',
   },
   navText: {
     fontSize: 12,
     marginTop: 4,
   },
   
 });

export default DistilleryDetailScreen;
